import { useState } from 'react';
import type { Editor } from '@tiptap/react';
import { Flex, IconButton, Icon, Text, icon as iconSize } from '@nula/core-ui';
import { InlineInputPopover } from './inline-input-popover';
import { FloatingPanel } from '@/components/floating-panel/floating-panel';
import styles from './editor-link-editor.module.css';

export interface EditorLinkEditorPosition {
  top: number;
  left: number;
}

export interface EditorLinkEditorProps {
  editor: Editor;
  isOpen: boolean;
  /** insert = link url input; view = show existing link with edit/remove */
  mode: 'insert' | 'view';
  position: EditorLinkEditorPosition;
  onClose: () => void;
  onSwitchToEdit?: () => void;
}

export function EditorLinkEditor({
  editor,
  isOpen,
  mode,
  position,
  onClose,
  onSwitchToEdit,
}: EditorLinkEditorProps) {
  const [url, setUrl] = useState(() => {
    if (mode === 'view' || mode === 'insert') {
      const attrs = editor.getAttributes('link');
      return (attrs?.href as string) ?? '';
    }
    return '';
  });

  if (!isOpen) return null;

  const handleConfirm = () => {
    const href = url.trim();
    if (href) {
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    }
    onClose();
  };

  const handleRemove = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    onClose();
  };

  if (mode === 'view') {
    const href = editor.getAttributes('link')?.href as string | undefined;
    return (
      <FloatingPanel top={position.top} left={position.left}>
        <Flex align="center" gap="sm" className={styles.viewPanel}>
          <Text size="sm" className={styles.linkUrl}>{href ?? ''}</Text>
          {href && (
            <IconButton
              size="sm"
              transparent
              pill
              aria-label="Open link"
              onClick={() => window.open(href, '_blank', 'noopener,noreferrer')}
            >
              <Icon name="open_in_new" size={iconSize.sm} weight={300} />
            </IconButton>
          )}
          {onSwitchToEdit && (
            <IconButton
              size="sm"
              transparent
              pill
              aria-label="Edit link"
              onClick={onSwitchToEdit}
            >
              <Icon name="edit" size={iconSize.sm} weight={300} />
            </IconButton>
          )}
          <IconButton
            size="sm"
            transparent
            pill
            aria-label="Remove link"
            onClick={handleRemove}
          >
            <Icon name="link_off" size={iconSize.sm} weight={300} />
          </IconButton>
        </Flex>
      </FloatingPanel>
    );
  }

  // insert/edit mode
  return (
    <InlineInputPopover
      top={position.top}
      left={position.left}
      label="URL"
      value={url}
      onChange={setUrl}
      onConfirm={handleConfirm}
      onCancel={onClose}
      leadingIcon="link"
      submitIcon="check"
      submitLabel="Apply link"
      cancelLabel="Cancel"
      inputWidthInSpace7={6}
    />
  );
}
